import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import type { RestaurantCompletedSaleDto } from '../../../../services/restaurantApiService';

/**
 * Garson raporu: ödenmiş adisyonların son ürün listesinden (ItemsJson) her ürünü
 * sepete ekleyen garsona (addedBy) göre toplar. Silinen ürünler ve azaltılan
 * miktarlar son listede zaten yer almaz; iptal adisyonları çağıran taraf eler.
 * Ayrı istek atılmaz: raporun indirdiği tamamlanan satışlar kullanılır.
 */

export interface WaiterProductLine {
  productName: string;
  saleCount: number;
  quantity: number;
  totalPrice: number;
}

export interface WaiterSaleLine {
  saleId: number;
  saleNumber: string;
  tableName: string;
  completedAt: string;
  quantity: number;
  totalPrice: number;
  items: { productName: string; quantity: number; totalPrice: number }[];
}

export interface WaiterSummary {
  key: string;
  name: string;
  isUnknown: boolean;
  saleCount: number;
  productTypeCount: number;
  quantity: number;
  totalPrice: number;
  share: number; // 0-100
  products: WaiterProductLine[];
  sales: WaiterSaleLine[];
}

const UNKNOWN_KEY = '__unknown__';

const pick = (o: any, ...names: string[]) => {
  for (const n of names) {
    if (o?.[n] !== undefined && o?.[n] !== null) return o[n];
  }
  return undefined;
};

const num = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLocaleUpperCase('tr-TR');

const round2 = (n: number) => Math.round(n * 100) / 100;

const parseItems = (itemsJson: string | null | undefined): any[] => {
  if (!itemsJson) return [];
  try {
    const parsed = JSON.parse(itemsJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export function buildWaiterSummaries(sales: RestaurantCompletedSaleDto[], unknownLabel: string): WaiterSummary[] {
  type Acc = {
    name: string;
    products: Map<string, WaiterProductLine & { sales: Set<number> }>;
    sales: Map<number, WaiterSaleLine>;
    quantity: number;
    totalPrice: number;
  };
  const byWaiter = new Map<string, Acc>();

  for (const sale of sales) {
    for (const raw of parseItems(sale.ItemsJson)) {
      const status = String(pick(raw, 'status', 'Status') ?? '').toLowerCase();
      if (status === 'cancelled' || status === 'canceled') continue;

      const productName = String(pick(raw, 'productName', 'ProductName', 'name', 'Name') ?? '').trim();
      const quantity = num(pick(raw, 'quantity', 'Quantity'));
      if (!productName || quantity <= 0) continue;
      let totalPrice = num(pick(raw, 'totalPrice', 'TotalPrice'));
      if (totalPrice === 0) totalPrice = quantity * num(pick(raw, 'unitPrice', 'UnitPrice', 'price', 'Price'));

      const waiterRaw = String(pick(raw, 'addedBy', 'AddedBy') ?? '').trim();
      const key = waiterRaw ? normalize(waiterRaw) : UNKNOWN_KEY;

      let acc = byWaiter.get(key);
      if (!acc) {
        acc = { name: waiterRaw || unknownLabel, products: new Map(), sales: new Map(), quantity: 0, totalPrice: 0 };
        byWaiter.set(key, acc);
      }
      acc.quantity += quantity;
      acc.totalPrice += totalPrice;

      const pKey = normalize(productName);
      let p = acc.products.get(pKey);
      if (!p) {
        p = { productName, saleCount: 0, quantity: 0, totalPrice: 0, sales: new Set() };
        acc.products.set(pKey, p);
      }
      p.quantity += quantity;
      p.totalPrice += totalPrice;
      p.sales.add(sale.Id);

      let s = acc.sales.get(sale.Id);
      if (!s) {
        s = {
          saleId: sale.Id,
          saleNumber: sale.SaleNumber || '',
          tableName: sale.TableName || '',
          completedAt: sale.CompletedAt,
          quantity: 0,
          totalPrice: 0,
          items: [],
        };
        acc.sales.set(sale.Id, s);
      }
      s.quantity += quantity;
      s.totalPrice += totalPrice;
      s.items.push({ productName, quantity, totalPrice: round2(totalPrice) });
    }
  }

  const grandTotal = Array.from(byWaiter.values()).reduce((sum, a) => sum + a.totalPrice, 0);

  return Array.from(byWaiter.entries())
    .map(([key, a]) => ({
      key,
      name: a.name,
      isUnknown: key === UNKNOWN_KEY,
      saleCount: a.sales.size,
      productTypeCount: a.products.size,
      quantity: round2(a.quantity),
      totalPrice: round2(a.totalPrice),
      share: grandTotal > 0 ? (a.totalPrice / grandTotal) * 100 : 0,
      products: Array.from(a.products.values())
        .map(({ sales: set, ...p }) => ({ ...p, saleCount: set.size, quantity: round2(p.quantity), totalPrice: round2(p.totalPrice) }))
        .sort((x, y) => y.totalPrice - x.totalPrice || y.quantity - x.quantity),
      sales: Array.from(a.sales.values())
        .map((s) => ({ ...s, quantity: round2(s.quantity), totalPrice: round2(s.totalPrice) }))
        .sort((x, y) => new Date(y.completedAt).getTime() - new Date(x.completedAt).getTime()),
    }))
    // Bilinmeyen en altta, diğerleri tutara göre
    .sort((x, y) => Number(x.isUnknown) - Number(y.isUnknown) || y.totalPrice - x.totalPrice);
}

/** Tamamlanan satış zamanı UTC gelir; rapordaki diğer sekmeler gibi +3 saat gösterilir. */
export const formatSaleTime = (utc: string) => {
  try {
    const d = new Date(new Date(utc).getTime() + 3 * 60 * 60 * 1000);
    return new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
  } catch {
    return utc;
  }
};

export interface WaiterExcelLabels {
  sheetSummary: string;
  sheetProducts: string;
  sheetSales: string;
  waiter: string;
  sales: string;
  productTypes: string;
  quantity: string;
  total: string;
  share: string;
  product: string;
  avgPrice: string;
  amount: string;
  saleNumber: string;
  table: string;
  date: string;
  items: string;
}

const styleHeader = (ws: ExcelJS.Worksheet) => {
  ws.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF283593' } };
  });
};

/** Garson listesi (+ her garsonun ürünleri) ya da tek garson (ürünler + adisyonlar) Excel'i. */
export async function exportWaiterExcel(summaries: WaiterSummary[], labels: WaiterExcelLabels, fileName: string, single = false) {
  const wb = new ExcelJS.Workbook();

  if (!single) {
    const ws = wb.addWorksheet(labels.sheetSummary);
    ws.columns = [
      { header: labels.waiter, key: 'name', width: 24 },
      { header: labels.sales, key: 'sales', width: 12 },
      { header: labels.productTypes, key: 'types', width: 14 },
      { header: labels.quantity, key: 'qty', width: 14 },
      { header: labels.total, key: 'total', width: 16 },
      { header: labels.share, key: 'share', width: 10 },
    ];
    styleHeader(ws);
    summaries.forEach((w) =>
      ws.addRow({ name: w.name, sales: w.saleCount, types: w.productTypeCount, qty: w.quantity, total: w.totalPrice, share: Math.round(w.share * 10) / 10 }),
    );
  }

  const wp = wb.addWorksheet(labels.sheetProducts);
  wp.columns = [
    { header: labels.waiter, key: 'name', width: 24 },
    { header: labels.product, key: 'product', width: 32 },
    { header: labels.sales, key: 'sales', width: 12 },
    { header: labels.quantity, key: 'qty', width: 12 },
    { header: labels.avgPrice, key: 'avg', width: 14 },
    { header: labels.amount, key: 'total', width: 16 },
  ];
  styleHeader(wp);
  summaries.forEach((w) =>
    w.products.forEach((p) =>
      wp.addRow({ name: w.name, product: p.productName, sales: p.saleCount, qty: p.quantity, avg: p.quantity > 0 ? round2(p.totalPrice / p.quantity) : 0, total: p.totalPrice }),
    ),
  );

  if (single) {
    const wsl = wb.addWorksheet(labels.sheetSales);
    wsl.columns = [
      { header: labels.saleNumber, key: 'no', width: 24 },
      { header: labels.table, key: 'table', width: 16 },
      { header: labels.date, key: 'date', width: 18 },
      { header: labels.items, key: 'items', width: 60 },
      { header: labels.quantity, key: 'qty', width: 12 },
      { header: labels.amount, key: 'total', width: 16 },
    ];
    styleHeader(wsl);
    summaries.forEach((w) =>
      w.sales.forEach((s) =>
        wsl.addRow({
          no: s.saleNumber,
          table: s.tableName,
          date: formatSaleTime(s.completedAt),
          items: s.items.map((i) => `${i.productName} x${i.quantity}`).join(', '),
          qty: s.quantity,
          total: s.totalPrice,
        }),
      ),
    );
  }

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
}
